import React, { useCallback, useContext, useEffect, useState } from 'react';
import Drawer from 'react-modern-drawer';
import { BASE_PATH } from '../..';
import { ReactComponent as ArrowRightIcon } from '../../assets/icons/arrow-right.svg';
import { ReactComponent as CloseIcon } from '../../assets/icons/close.svg';
import { ReactComponent as EmptyIcon } from '../../assets/icons/empty.svg';
import { Asset, ModelAsset, ModelpackAsset, Point, SceneDesignerContext } from '../../contexts/SceneDesignerContext';
import { ModelPackViewDTO, ModelViewDTO } from '../../openapi/requests';
import { ConfirmDialog } from '../layout/Dialog';
import './ModelConfigurator.scss';
import ProductSelector from './ProductSelector';
import BomDesigner from './BomDesigner';
import Overlay from '../layout/Overlay';
import { BomDesignerContext } from '../../contexts/BomDesignerContext';
import { AuthContext } from '../../contexts/AuthContext';


interface Props {
    open: boolean;
    point: Point | undefined;
    subPoint?: Point;
    onClose: () => void;
}

const ModelConfigurator: React.FC<Props> = ({ open, onClose, point, subPoint }) => {
    const { changeProduct } = useContext(SceneDesignerContext);
    const { createFromConfig } = useContext(BomDesignerContext);
    const { hasPermission, hasRole } = useContext(AuthContext);

    const [selectorOpen, setSelectorOpen] = useState(false);
    const [editorOpen, setEditorOpen] = useState(false);
    const [selectedPoint, setSelectedPoint] = useState<Point | undefined>(undefined);
    const [surfaceSelectorOpen, setSurfaceSelectorOpen] = useState(false);
    const [modelConfiguratorOpen, setModelConfiguratorOpen] = useState(false);

    const [showConfirm, setShowConfirm] = useState(false);
    const [products, setProducts] = useState<(ModelViewDTO | ModelPackViewDTO)[]>([]);
    const [working, setWorking] = useState(false);
    const [bomDesignerOpen, setBomDesignerOpen] = useState(false);

    useEffect(() => {
        if(subPoint){
            setSelectedPoint(subPoint);
            setSurfaceSelectorOpen(true);
            setModelConfiguratorOpen(true);
        }
    }, [subPoint])

    const handlePointClick = (point: Point) => {
        setSelectedPoint(point);
        setSurfaceSelectorOpen(true);
        setModelConfiguratorOpen(true);
    }

    useEffect(() => {
        if (point?.bomData && hasPermission("EditBoms")) {
            setEditorOpen(open);
            setSelectorOpen(false);
        } else {
            setSelectorOpen(open);
            setEditorOpen(false);
        }
    }, [hasPermission, open, point]);

    const handleMaterialChange = useCallback((products: Asset[]) => {
        if (selectedPoint) {
            changeProduct(selectedPoint, products, false);
        }
    }, [selectedPoint, changeProduct]);

    const handleModelChange = useCallback((products: Asset[]) => {
        if(point){
            setWorking(true);
            changeProduct(point, products, false).then(result => {
                setWorking(false);
                if(!result){
                    setProducts(products as (ModelAsset | ModelpackAsset)[]);
                    setShowConfirm(true);
                }else{
                    setSelectorOpen(false);
                    let models = products as ModelAsset[];
                    if (!models.some(e => e.isWhitemodel)) {
                        onClose();
                    }
                }
            });
        }
    }, [changeProduct, onClose, point]);

    const handleConfirm = useCallback(() => {
        if(point){
            setShowConfirm(false);
            setWorking(true);

            changeProduct(point, products as (ModelAsset | ModelpackAsset)[], true).then(() => {
                setWorking(false);
                setSelectorOpen(false);
                if (!point?.bomData) {
                    onClose();
                }
            });
        }
    }, [changeProduct, onClose, products, point]);

    const handleSelectorClose = useCallback(() => {
        setSelectorOpen(false);
        if (!point?.bomData) {
            onClose();
        }
    }, [onClose, point?.bomData]);

    const handleSaveClick = useCallback(() => {
        if(point && point.type === "model"){
            setBomDesignerOpen(true);
            createFromConfig(point.configuration);
        }
    }, [createFromConfig, point]);

    const flatten = (point: Point) => {
        const items: Point[] = [];
      
        point.children.forEach(item => {
            items.push(item);
            items.push(...flatten(item));
        });
      
        return items;
    }

    const points = point ? flatten(point) : [];

    return (
        <>
            <ConfirmDialog onConfirm={handleConfirm} open={showConfirm} onCancel={() => setShowConfirm(false)} text='By making this selection, you are about the remove the attached items.' title='Are you sure?' />

            {(point !== undefined && point.type === 'model') &&
                <>
                    <Drawer direction='right' className='modelConfigurator' open={editorOpen} size={530} onClose={onClose} overlayOpacity={0} enableOverlay={false}>
                        <div className='header'>
                            <h1>Configure {point.configuration.placement.label}</h1>
                            <button className='close' onClick={onClose}>
                                <CloseIcon />
                            </button>
                        </div>

                        <div className='current-model'>
                            <div className='product-image'>
                                <span className='point-label'>{point.configuration.id}</span>
                                {point.configuration.value === undefined ?
                                    <EmptyIcon /> :
                                    <img src={BASE_PATH + point.configuration.value.thumbnail} alt='' />
                                }
                            </div>

                            <h3>{point.configuration.value?.title ?? "Empty"}</h3>
                        </div>

                        <div className='attributes'>

                            {point.configuration.value?.metadata.some(e => e.name === 'ean') && <div className='attribute'>
                                <div>
                                    <label>Serial Number</label>
                                    {point.configuration.value?.metadata.find(e => e.name === 'ean')?.value}
                                </div>
                                <svg className='line' width="1000" height="2" viewBox="0 0 1000 2" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path opacity="0.5" d="M1000 1H0" stroke="black" strokeOpacity="0.5" strokeDasharray="2 2" />
                                </svg>
                            </div>}

                            <button onClick={() => setSelectorOpen(true)}>
                                <span>Change model</span>
                                <ArrowRightIcon />
                            </button>

                            {(hasRole("Cadesign") && point.bomData) && 
                                <button onClick={handleSaveClick}>
                                    <span>Save configuration as BOM</span>
                                </button>
                            }

                            <h2>Attached Items ({points.length})</h2>
                            <div className='model-items'>
                                {points.filter(e => e.hidden).map(item => <div className='item' onClick={() => handlePointClick(item)} key={item.configuration.placement.name}>
                                    <div className='product-image'>
                                        <span className='point-label'>{item.label} ({item.configuration.id})</span>
                                        {item.configuration.value === undefined ?
                                            <EmptyIcon /> :
                                            <img src={BASE_PATH + item.configuration.value.thumbnail} alt='' />
                                        }
                                    </div>
                                </div>)}
                            </div>
                        </div>
                    </Drawer>

                    <Overlay open={bomDesignerOpen} onClose={() => setBomDesignerOpen(false)}>
                        <BomDesigner onSave={() => {}} />
                    </Overlay>

                    <ProductSelector
                        open={selectorOpen}
                        point={point}
                        working={working}
                        onCancel={handleSelectorClose}
                        onProductSelected={handleModelChange} />

                    {selectedPoint && selectedPoint.type !== "model" && <ProductSelector
                        open={surfaceSelectorOpen}
                        point={selectedPoint}
                        onCancel={() => setSurfaceSelectorOpen(false)}
                        onProductSelected={handleMaterialChange} />}

                    {selectedPoint && selectedPoint.type === "model" && <ModelConfigurator
                        point={selectedPoint}
                        onClose={() => setModelConfiguratorOpen(false)}
                        open={modelConfiguratorOpen}
                    />}
                </>
            }
        </>
    );
};

export default ModelConfigurator;